/* eslint-disable @typescript-eslint/no-unused-vars */
// TODO: Fix unused vars

import colorLib from '@kurkle/color';
import { type Chart, type ChartData, type ChartOptions } from 'chart.js';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { LoadingComponent } from '../../../../components/loading/loading.component';
import {
  emphasizeColor,
  fadeColor,
  getAspectRatio,
  getLegend,
} from '../../../../components/reports/common/helpers/chart-options.helper';
import { LegendPaddingPlugin } from '../../../../components/reports/common/plugins/legend-padding.plugin';
import { ExportButtonDownloader } from '../../../../components/reports/components/export-button/export-button.component';
import { usePDFDownloader } from '../../../../components/reports/hooks/use-pdf-downloader.hook';
import { ReportsComponentProps } from '../../../../components/reports/interfaces/reports-component-props';
import { useDataProvider } from '../../../../hooks/data-provider/data-provider.hook';
import { useResize } from '../../../../hooks/use-window-resize.hook';
import { SortBy } from '../../../../interfaces/sort-by.interface';
import { BRAND_COLORS } from '../../../../static-resources/static-misc';
import { formatBigNumber } from '../../../../utils/formatting/number-formatter.helper';
import '../../../../components/reports/scss/chart-component.scss';
import './chart-component.scss';
import 'chart.js/auto';

const FALLBACK_COLORS = [
  'rgba(255, 140, 0, 1)', // Dark Orange
  'rgba(46, 139, 87, 1)', // Sea Green
  'rgba(148, 0, 211, 1)', // Dark Violet
  'rgba(210, 105, 30, 1)', // Chocolate
  'rgba(0, 191, 255, 1)', // Deep Sky Blue
];

interface ColorCache {
  [key: string]: {
    baseColor: string;
    subColors: string[];
  };
}

const ColorManager = {
  colorCache: {} as ColorCache,

  getBaseColor(group: string): string {
    return (
      BRAND_COLORS[group] ||
      FALLBACK_COLORS[
        Object.keys(this.colorCache).length % FALLBACK_COLORS.length
      ]
    );
  },

  getFadedColor(baseColor: string, index: number, total: number): string {
    const minOpacity = 0.3;
    const maxFade = 0.7;

    const fadeStep = (maxFade - minOpacity) / Math.max(total - 1, 1);
    const fadeFactor = minOpacity + fadeStep * index;

    if (index === 0) return baseColor;

    return colorLib(baseColor)
      .lighten(1 - fadeFactor)
      .rgbString();
  },

  getColorsForGroup(group: string, itemCount: number): string[] {
    if (!this.colorCache[group]) {
      const baseColor = this.getBaseColor(group);
      const subColors = Array.from({ length: itemCount }, (_, i) =>
        this.getFadedColor(baseColor, i, itemCount),
      );

      this.colorCache[group] = {
        baseColor,
        subColors,
      };
    }
    return this.colorCache[group].subColors;
  },

  clearCache() {
    this.colorCache = {};
  },
};

interface DemandModelChartComponentProps extends ReportsComponentProps {
  defaultSortBy?: SortBy[];
  setPDFDownloader?(callback: ExportButtonDownloader): void;
}

const ChartComponent = ({
  defaultSortBy,
  reportName: reportId,
  filterSelection,
  enabled,
  className,
  setPDFDownloader,
}: DemandModelChartComponentProps) => {
  const [chartData, setChartData] = useState<ChartData<'line'>>({
    labels: [],
    datasets: [],
  });
  const [xAxisLabel, setXAxisLabel] = useState<string>(''); // State for dynamic X-axis label
  const [yAxisLabel, setYAxisLabel] = useState<string>(''); // State for dynamic Y-axis label
  const chartRef = useRef<Chart<'line'> | null>(null); // Ref for chart instance

  // Track highlighted datasets - when clicked on legend
  const [highlightedDatasets, setHighlightedDatasets] = useState<
    Record<number, boolean>
  >({});
  // Track if any dataset is currently highlighted
  const [hasHighlights, setHasHighlights] = useState<boolean>(false);

  // Use the useDataProvider hook to fetch the data, but only if enabled
  const { data, isFetching } = useDataProvider(
    reportId,
    filterSelection,
    defaultSortBy,
    enabled,
  );

  const pdfDownloader = usePDFDownloader(
    filterSelection,
    'IRIS-Demand-model',
    'IRIS Demand model',
    chartRef.current,
  );

  useEffect(() => {
    if (setPDFDownloader !== undefined) {
      setPDFDownloader(pdfDownloader);
    }
  }, [setPDFDownloader, pdfDownloader]);

  useEffect(() => {
    return () => ColorManager.clearCache();
  }, [data]);

  // Check if any dataset is highlighted whenever highlightedDatasets changes
  useEffect(() => {
    const anyHighlighted = Object.values(highlightedDatasets).some(
      (value) => value,
    );
    setHasHighlights(anyHighlighted);
  }, [highlightedDatasets]);

  // Handle legend click event
  const handleLegendClick = useCallback((e: MouseEvent, legendItem: any) => {
    if (legendItem && typeof legendItem.datasetIndex === 'number') {
      const index = legendItem.datasetIndex;

      setHighlightedDatasets((prev) => {
        const newState = { ...prev };

        // If clicking an already highlighted item, unhighlight it
        if (newState[index]) {
          delete newState[index];
        } else {
          // Otherwise highlight it
          newState[index] = true;
        }

        return newState;
      });
    }

    return false; // Prevent default behavior
  }, []);

  const transformData = useCallback(
    (data: any): ChartData<'line'> => {
      // Get X-axis column details
      const xAxisColumn = data.columns?.[1]; // Assuming the second column is for the X-axis
      const xAxisId = xAxisColumn?.id || 'WEEK';
      const xAxisFormat = xAxisColumn?.format || 'YYYY-MM-DD'; // Use the format or default to 'YYYY-MM-DD'
      const xAxisDatatype = xAxisColumn?.datatype;

      // Dynamically extract the measure ID from the measureColumns
      const measureId = data.measureColumns?.[0]?.id || 'Amount'; // Fallback to 'Amount' if not found

      // Extract brandId based on the first column's id
      const brandId = data.columns?.[0]?.id || 'IRIS_BRAND';
      const brandIdField = data.columns?.[0]?.idField || 'IRIS_BRAND';

      // Format labels based on datatype and format, and sort them
      const labels = Array.from(
        new Set<string>(
          data.rows.map((row: any) => {
            const value = row[xAxisId];
            if (xAxisDatatype === 'DATETIME' && xAxisFormat) {
              return moment(value).format(xAxisFormat); // Fallback to ISO format if parsing fails
            }
            return value;
          }),
        ),
      );
      //        .sort((a, b)=> a > b ? 1 : -1); // Ensures correct x axis order

      // Pre-process groups and count items
      const groupItems: Record<string, string[]> = {};
      data.rows.forEach((row: any) => {
        const group = row.metadata?.descriptors?.[0] || row[brandId] || 'Other';
        const brand = row[brandId];
        if (!groupItems[group]) {
          groupItems[group] = [];
        }
        if (!groupItems[group].includes(brand)) {
          groupItems[group].push(brand);
        }
      });

      // Create datasets with consistent colors and sort by brand name
      const seriesDict: Record<string, any> = {};
      const dateRows = data.rows;
      for (let index = 0; index < dateRows.length; index++) {
        const row = dateRows[index];
        const prodHierLabel = row[brandId];
        const prodHierCode = row[brandIdField];
        const xAxisValue: string = moment(row[xAxisId]).format(xAxisFormat);

        const value = row[measureId];
        let serie: any = seriesDict[prodHierCode];
        if (!serie) {
          // For each product hierarchy element (brand, sub brand, ect.) we create an object in the first loop.
          // Gathering color information.
          const group =
            row.metadata?.descriptors?.[0] || row[brandId] || 'Other';
          const groupBrands = groupItems[group];
          const brandIndex = groupBrands.indexOf(prodHierLabel);
          const groupColors = ColorManager.getColorsForGroup(
            group,
            groupBrands.length,
          );
          const dataDict: Record<string, any> = {};
          // To preserve exact same order as labels, we create a dictionary for x axis.
          for (let j = 0; j < labels.length; j++) {
            // All series must have same number of items even if there is no data for that date.
            dataDict[labels[j]] = null;
          }
          // The serie is initialized.
          serie = {
            label: prodHierLabel,
            borderColor: groupColors[brandIndex],
            backgroundColor: 'rgba(0, 123, 255, 0.1)', // Set line background color to visible
            pointBackgroundColor: groupColors[brandIndex],
            pointBorderColor: groupColors[brandIndex],
            group,
            dataDict,
          };
        }
        // Store the value in the dictionary.
        serie.dataDict[xAxisValue] = value;
        // Store the serie in the dictionary.
        seriesDict[prodHierCode] = serie;
      }
      const datasets = Object.entries(seriesDict).map(
        ([key, serie], datasetIndex) => {
          const data: (number | null)[] = [];
          // With this loop we ensure the order of the data is the same than the order of the labels (x axis values).
          for (let j = 0; j < labels.length; j++) {
            const xAxisValue = labels[j];
            data.push(serie.dataDict[xAxisValue]);
          }

          const isHighlighted = highlightedDatasets[datasetIndex] || false;
          const shouldFade = hasHighlights && !isHighlighted;
          let borderColor = serie.borderColor;
          let pointBackgroundColor = serie.pointBackgroundColor;
          let pointBorderColor = serie.pointBorderColor;
          let borderWidth = 2;
          let pointRadius = 3;
          let pointHoverRadius = 6;
          let zIndex = 0;

          // Apply highlight styling if this dataset is highlighted
          if (isHighlighted) {
            borderColor = emphasizeColor(serie.borderColor);
            pointBackgroundColor = emphasizeColor(serie.pointBackgroundColor);
            pointBorderColor = emphasizeColor(serie.pointBorderColor);
            borderWidth = 3; // Thicker line for highlighted datasets
            pointRadius = 4; // Larger points
            pointHoverRadius = 7; // Larger hover radius
            zIndex = 10; // Bring to front
          }
          // Apply faded styling if other datasets are highlighted but not this one
          else if (shouldFade) {
            borderColor = fadeColor(serie.borderColor);
            pointBackgroundColor = fadeColor(serie.pointBackgroundColor);
            pointBorderColor = fadeColor(serie.pointBorderColor);
            borderWidth = 1; // Thinner line
            pointRadius = 2; // Smaller points
            pointHoverRadius = 4; // Smaller hover radius
            zIndex = 1; // Send to back
          }

          return {
            label: serie.label,
            data,
            borderColor,
            backgroundColor: 'rgba(0, 123, 255, 0.1)', // Set line background color to visible
            pointBackgroundColor,
            pointBorderColor,
            group: serie.group,
            fill: false,
            pointRadius,
            pointHoverRadius,
            borderWidth,
            zIndex,
          };
        },
      );

      return {
        labels, // Use the sorted labels for the X-axis
        datasets: datasets as ChartData<'line'>['datasets'],
      };
    },
    [hasHighlights, highlightedDatasets],
  );

  useEffect(() => {
    if (enabled && data) {
      const transformedData = transformData(data);
      setChartData(transformedData);

      // Set the dynamic Y-axis and X-axis labels from the columns
      if (data.columns && data.columns.length > 1) {
        setXAxisLabel(data.columns[1].name); // Use the name from data.columns[1] for the X-axis label
      }
      if (data.measureColumns && data.measureColumns.length > 0) {
        setYAxisLabel(data.measureColumns[0].name); // Use the name from measureColumns for the Y-axis label
      }
    }
  }, [enabled, data, highlightedDatasets, hasHighlights, transformData]);

  const xAxisFormat = data?.columns?.[1]?.format || 'YYYY-MM-DD'; // Determine xAxisFormat from data

  const options: ChartOptions<'line'> = useMemo(() => {
    // Get base legend options with custom click handler
    const legendOptions = getLegend(10, handleLegendClick);

    // Enhanced legend labels with custom styling for highlighted items
    const enhancedLegendOptions = {
      ...legendOptions,
      labels: {
        ...legendOptions.labels,
        filter: (legendItem: any) => {
          const index = legendItem.datasetIndex;
          const isHighlighted =
            index !== undefined && highlightedDatasets[index];

          if (isHighlighted) {
            // Make highlighted legend items bold or otherwise distinguished
            legendItem.fontColor = '#000'; // Darker text
            legendItem.fontStyle = 'bold';
          } else if (hasHighlights) {
            // Dim the non-highlighted legend items
            legendItem.fontColor = 'rgba(0, 0, 0, 0.5)';
          }

          return true; // Keep all legend items
        },
      },
    };

    return {
      responsive: true,
      aspectRatio: getAspectRatio(),
      scales: {
        x: {
          title: {
            display: true,
            text: xAxisLabel,
            font: {
              size: 10,
            },
          },
          ticks: {
            font: {
              size: 9,
            },
            callback: function (tickValue: string | number, index: number) {
              const label = chartData.labels?.[index];

              if (label && typeof label === 'string') {
                const formattedDate = moment(label, xAxisFormat, true);
                if (formattedDate.isValid()) {
                  return formattedDate.format(xAxisFormat);
                } else {
                  return 'Invalid date';
                }
              }
              return tickValue;
            },
          },
          grid: {
            display: false,
          },
        },
        y: {
          beginAtZero: true, // Always show zero
          title: {
            display: true,
            text: yAxisLabel,
            font: {
              size: 10,
            },
          },
          ticks: {
            font: {
              size: 9, // Reduced font size for Y-axis labels
            },
            callback: function (tickValue: any) {
              return formatBigNumber(tickValue); // Format Y-axis values
            },
          },
          grid: {
            display: false, // Remove the grid lines on the Y-axis
          },
        },
      },
      elements: {
        line: {
          tension: 0.4, // Smoother lines
          borderColor: 'rgba(0, 123, 255, 0.8)', // Set line border color to visible
          backgroundColor: 'rgba(0, 123, 255, 0.1)', // Set line background color to visible
        },
        point: {
          borderWidth: 1, // Set point border width
          radius: 2, // Set point radius
          hoverRadius: 3, // Increase point radius on hover
        },
      },
      plugins: {
        // Uses circles for legends with custom click handler
        legend: enhancedLegendOptions,
        tooltip: {
          enabled: true, // Enable tooltips on hover
          mode: 'nearest',
          intersect: false, // Tooltip appears for nearest point, even if not intersecting
          callbacks: {
            label: function (context: any) {
              const label = context.dataset.label || '';
              const value = context.raw;
              return `${label}: ${formatBigNumber(value)}`;
            },
          },
        },
      },
      layout: {
        padding: {
          left: 0,
          right: 0,
        },
      },
    };
  }, [
    chartData.labels,
    xAxisFormat,
    xAxisLabel,
    yAxisLabel,
    handleLegendClick,
    highlightedDatasets,
    hasHighlights,
  ]);

  // This hook help us to resize/update the chart when windows size changes.
  useResize(chartRef.current);

  if (!enabled) {
    return <div className="chart-component__disabled">Chart is disabled</div>;
  }
  return (
    <div
      className={`chart-component ${className ?? ''}`}
      style={{ backgroundColor: 'transparent', border: 'none' }}
    >
      <LoadingComponent isLoading={isFetching}>
        {/* <div ref={legendContainerRef} className="chart-component__legend">
                    {legendRows}
                </div> */}
        <Line
          ref={chartRef}
          data={chartData}
          options={options}
          // Workaround: this plugin adds some space between legends and chart.
          plugins={[LegendPaddingPlugin]}
        />
      </LoadingComponent>
    </div>
  );
};

export default ChartComponent;
